import { getAdminMeOptions, getCurrentAdminRbacPermissionsOptions } from '@gozoki/api'
import { createFileRoute, Outlet } from '@tanstack/react-router'
import { CommandPaletteProvider } from '~/components/new/command-palette/command-palette'
import { StoreSwitchProvider } from '~/components/new/store-switch/store-switch-provider'
import { ScrollArea } from '~/components/ui/scroll-area'
import { createAppAbility } from '~/utils/ability'
import { parseRbacContract } from '~/utils/rbac-session'
import { AppSidebar } from '../../components/new/navigation/app-sidebar'
import { RubriqueTabsBar } from '../../components/new/navigation/rubrique-tabs-bar'
import { usePinnedSidebar } from '../../components/new/navigation/usePinnedSidebar'
import { SidebarInset, SidebarProvider } from '../../components/ui/sidebar'

export const Route = createFileRoute('/_authenticated')({
  component: AuthenticatedLayout,
  beforeLoad: async ({ context }) => {
    // Les grants v2 du contrat de l'admin connecté décident seuls. Une réponse absente, en erreur
    // (502 passager) ou de forme incompatible (BO déployé avant le backend) ne donne aucun droit :
    // l'ability est vide, le `GlobalProvider` affiche « Autorisations indisponibles », relance la
    // requête et recharge les routes dès qu'un contrat exploitable arrive.
    const admin = await context.queryClient.ensureQueryData(getAdminMeOptions()).catch(() => null)
    const rbacPermissions = admin
      ? await context.queryClient
          .ensureQueryData(getCurrentAdminRbacPermissionsOptions(admin.id))
          .catch(() => null)
      : null

    return { ability: createAppAbility(parseRbacContract(rbacPermissions)?.grantsV2) }
  },
})

function AuthenticatedLayout() {
  // Registre de la sélection rapide de boutique ⌘S : les sélecteurs des écrans s'y inscrivent.
  return (
    <StoreSwitchProvider>
      <AuthenticatedShell />
    </StoreSwitchProvider>
  )
}

function AuthenticatedShell() {
  // Menu replié par défaut, ouvert au survol par-dessus la page ; épinglé ouvert, il pousse la page.
  // Le choix d'épinglage est gardé dans le navigateur, par admin.
  const [sidebarPinned, setSidebarPinned] = usePinnedSidebar()

  return (
    <div className="h-screen max-h-screen overflow-hidden leading-none">
      <ScrollArea className="h-full" scrollBarClassName="mr-px">
        <SidebarProvider open={sidebarPinned} onOpenChange={setSidebarPinned}>
          {/* Palette de navigation (⌘K / Ctrl+K) : sur toutes les pages authentifiées, dans le
              registre ⌘S (action « Changer de boutique ») et le menu (action qui le replie). */}
          <CommandPaletteProvider>
            <AppSidebar />
            {/* overflow-x-clip (pas overflow-hidden ni overflow-x-hidden) : le clip horizontal reste, mais
              le contenu qui dépasse en HAUTEUR remonte jusqu'à la ScrollArea du shell au lieu d'être
              rogné — sinon une page plus haute que le viewport (ex. dashboard RH avec cartes à
              min-height) se fait couper par le bas sans pouvoir scroller. `clip` plutôt que `hidden`
              car `hidden` fait de cet élément un scroll container : les enfants `position: sticky`
              s'ancreraient alors sur lui (qui ne scrolle jamais) au lieu de la ScrollArea du shell. */}
            <SidebarInset className="m-2 overflow-x-clip">
              <RubriqueTabsBar />
              <Outlet />
            </SidebarInset>
          </CommandPaletteProvider>
        </SidebarProvider>
      </ScrollArea>
    </div>
  )
}
